<template>
  <div class="coal-shed-layout">
    <header class="coal-ribbon">
      <div class="ribbon-inner">
        <div class="ribbon-group">
          <span class="ribbon-group-title">绘制</span>
          <div class="ribbon-btns">
            <button type="button" :class="['ribbon-btn', { 'is-active': tool === 'zone' }]" @click="setTool('zone')">
              <span class="ribbon-btn-ico" aria-hidden="true">▢</span>
              <span class="ribbon-btn-text">划分库区</span>
            </button>
            <button type="button" :class="['ribbon-btn', { 'is-active': tool === 'stack' }]" @click="setTool('stack')">
              <span class="ribbon-btn-ico" aria-hidden="true">▣</span>
              <span class="ribbon-btn-text">划分堆位</span>
            </button>
            <button type="button" :class="['ribbon-btn', { 'is-active': tool === 'select' }]" @click="setTool('select')">
              <span class="ribbon-btn-ico" aria-hidden="true">↖</span>
              <span class="ribbon-btn-text">选择 / 编辑</span>
            </button>
          </div>
        </div>
        <div class="ribbon-sep" />
        <div class="ribbon-group">
          <span class="ribbon-group-title">录入</span>
          <div class="ribbon-btns">
            <button
              type="button"
              :class="['ribbon-btn', { 'is-active': dropOpen && dropKind === 'manualZone' }]"
              @click="toggleDrop('manualZone')"
            >
              <span class="ribbon-btn-ico" aria-hidden="true">⊞</span>
              <span class="ribbon-btn-text">手动库区</span>
            </button>
            <button
              type="button"
              :class="['ribbon-btn', { 'is-active': dropOpen && dropKind === 'manualStack' }]"
              @click="toggleDrop('manualStack')"
            >
              <span class="ribbon-btn-ico" aria-hidden="true">⊡</span>
              <span class="ribbon-btn-text">手动堆位</span>
            </button>
          </div>
        </div>
        <div class="ribbon-sep" />
        <div class="ribbon-group">
          <span class="ribbon-group-title">视图</span>
          <div class="ribbon-btns ribbon-btns--row">
            <button type="button" class="ribbon-btn ribbon-btn--compact" title="缩小" @click="zoomOut">
              <span class="ribbon-btn-ico" aria-hidden="true">−</span>
              <span class="ribbon-btn-text">缩小</span>
            </button>
            <span class="zoom-readout">{{ Math.round(canvasZoom * 100) }}%</span>
            <button type="button" class="ribbon-btn ribbon-btn--compact" title="放大" @click="zoomIn">
              <span class="ribbon-btn-ico" aria-hidden="true">+</span>
              <span class="ribbon-btn-text">放大</span>
            </button>
            <button type="button" class="ribbon-btn ribbon-btn--compact" title="100%" @click="zoomReset">
              <span class="ribbon-btn-text">重置</span>
            </button>
          </div>
        </div>
        <div class="ribbon-sep" />
        <p class="ribbon-hint">{{ toolHint }}</p>
      </div>

      <transition name="coal-drop">
        <div v-show="dropOpen" class="coal-drop-panel">
          <div class="drop-panel-head">
            <span class="drop-title">{{ dropPanelTitle }}</span>
            <button type="button" class="drop-close" aria-label="关闭" @click="closeDropPanel">×</button>
          </div>
          <div class="drop-panel-body">
            <!-- 手动添加库区 -->
            <template v-if="dropKind === 'manualZone'">
              <el-form size="small" label-width="96px" class="drop-form inline-form">
                <el-form-item label="煤棚">
                  <el-select v-model="mzShed" style="width:200px">
                    <el-option :label="shedOptionLabel('west')" value="west" :disabled="!westStore" />
                    <el-option :label="shedOptionLabel('east')" value="east" :disabled="!eastStore" />
                  </el-select>
                </el-form-item>
                <el-form-item label="库区名称">
                  <el-input v-model="mzName" placeholder="如 A区" style="width:200px" />
                </el-form-item>
                <el-form-item label="西东起点">
                  <el-input-number v-model="mzX" :step="0.1" :min="0" :max="ew" controls-position="right" />
                </el-form-item>
                <el-form-item label="北南起点">
                  <el-input-number v-model="mzY" :step="0.1" :min="0" :max="ns" controls-position="right" />
                </el-form-item>
                <el-form-item label="东西宽">
                  <el-input-number v-model="mzW" :step="0.1" :min="0.5" :max="ew" controls-position="right" />
                </el-form-item>
                <el-form-item label="南北长">
                  <el-input-number v-model="mzH" :step="0.1" :min="0.5" :max="ns" controls-position="right" />
                </el-form-item>
                <el-form-item>
                  <el-button type="primary" size="small" @click="addZoneManual">保存</el-button>
                </el-form-item>
              </el-form>
            </template>

            <!-- 手动添加堆位 -->
            <template v-else-if="dropKind === 'manualStack'">
              <el-form size="small" label-width="96px" class="drop-form inline-form">
                <el-form-item label="煤棚">
                  <el-select v-model="msShed" style="width:200px" @change="syncMsZone">
                    <el-option :label="shedOptionLabel('west')" value="west" :disabled="!westStore" />
                    <el-option :label="shedOptionLabel('east')" value="east" :disabled="!eastStore" />
                  </el-select>
                </el-form-item>
                <el-form-item label="所属库区">
                  <el-select v-model="msZoneId" style="width:200px" filterable>
                    <el-option v-for="z in zonesOfShed(msShed)" :key="z.cid" :label="z.name" :value="z.cid" />
                  </el-select>
                </el-form-item>
                <el-form-item label="堆位名称">
                  <el-input v-model="msName" placeholder="如 1号堆" style="width:200px" />
                </el-form-item>
                <el-form-item label="西东起点">
                  <el-input-number v-model="msX" :step="0.1" :min="0" :max="ew" controls-position="right" />
                </el-form-item>
                <el-form-item label="北南起点">
                  <el-input-number v-model="msY" :step="0.1" :min="0" :max="ns" controls-position="right" />
                </el-form-item>
                <el-form-item label="东西宽">
                  <el-input-number v-model="msW" :step="0.1" :min="0.5" controls-position="right" />
                </el-form-item>
                <el-form-item label="南北长">
                  <el-input-number v-model="msH" :step="0.1" :min="0.5" controls-position="right" />
                </el-form-item>
                <el-form-item>
                  <el-button type="primary" size="small" @click="addStackManual">保存</el-button>
                </el-form-item>
              </el-form>
            </template>

            <!-- 编辑已选库区 -->
            <template v-else-if="dropKind === 'editZone' && propZone">
              <el-form size="small" label-width="100px" class="drop-form inline-form">
                <el-form-item label="煤棚"><span class="drop-readonly">{{ shedTitle(selectedShed) }}</span></el-form-item>
                <el-form-item label="库区名称"><el-input v-model="propZone.name" style="width:200px" /></el-form-item>
                <el-form-item label="西东起点"><el-input-number v-model="propZone.x" :step="0.1" controls-position="right" /></el-form-item>
                <el-form-item label="北南起点"><el-input-number v-model="propZone.y" :step="0.1" controls-position="right" /></el-form-item>
                <el-form-item label="东西宽"><el-input-number v-model="propZone.w" :step="0.1" controls-position="right" /></el-form-item>
                <el-form-item label="南北长"><el-input-number v-model="propZone.h" :step="0.1" controls-position="right" /></el-form-item>
                <el-form-item>
                  <el-button type="primary" size="small" @click="applyZone">保存</el-button>
                  <el-button type="danger" size="small" plain @click="removeSelectedZone">删除</el-button>
                </el-form-item>
              </el-form>
              <div class="drop-subblock">
                <span class="drop-subtitle">网格生成堆位</span>
                <el-form size="small" class="drop-form inline-form" inline>
                  <el-form-item label="行(南北)"><el-input-number v-model="gridRows" :min="1" :max="50" controls-position="right" /></el-form-item>
                  <el-form-item label="列(东西)"><el-input-number v-model="gridCols" :min="1" :max="50" controls-position="right" /></el-form-item>
                  <el-form-item>
                    <el-button size="small" @click="gridStacks">按网格生成</el-button>
                    <el-button size="small" type="danger" plain @click="clearStacksInZone">清空堆位</el-button>
                  </el-form-item>
                </el-form>
              </div>
            </template>

            <!-- 编辑已选堆位：未关联合同时仅维护几何与名称；关联合同后展示合同与客户、库存 -->
            <template v-else-if="dropKind === 'editStack' && propStack">
              <el-form size="small" label-width="100px" class="drop-form inline-form">
                <el-form-item label="煤棚"><span class="drop-readonly">{{ shedTitle(selectedShed) }}</span></el-form-item>
                <el-form-item v-if="stackPanelDisabled" label="状态"><span class="drop-readonly warn">已禁用</span></el-form-item>
                <el-form-item label="堆位名称"><el-input v-model="propStack.locationName" style="width:200px" /></el-form-item>
                <el-form-item label="西东起点"><el-input-number v-model="propStack.layoutX" :step="0.1" controls-position="right" /></el-form-item>
                <el-form-item label="北南起点"><el-input-number v-model="propStack.layoutY" :step="0.1" controls-position="right" /></el-form-item>
                <el-form-item label="东西宽"><el-input-number v-model="propStack.layoutW" :step="0.1" controls-position="right" /></el-form-item>
                <el-form-item label="南北长"><el-input-number v-model="propStack.layoutH" :step="0.1" controls-position="right" /></el-form-item>
                <el-form-item label="理论容量(吨)">
                  <span class="drop-readonly">{{ stackTheoryCap }}</span>
                  <span class="dim">（占地×30m×1t/m³）</span>
                </el-form-item>
                <el-form-item label="库存容量上限">
                  <div class="cap-row">
                    <el-switch
                      v-model="propStack.capOverrideEnabled"
                      active-text="自定义上限"
                      inactive-text="按公式"
                      @change="onCapOverrideSwitch"
                    />
                    <el-input-number
                      v-if="propStack.capOverrideEnabled"
                      v-model="propStack.stockCapTons"
                      :min="0.001"
                      :step="1"
                      :precision="3"
                      controls-position="right"
                      class="cap-input"
                    />
                  </div>
                </el-form-item>
                <template v-if="editStackShowBizFields">
                  <el-form-item label="合同扩展ID"><el-input v-model="propStack.contractExtId" placeholder="transport_contract_ext.id" style="width:200px" /></el-form-item>
                  <el-form-item label="客户编码"><el-input v-model="propStack.custNo" style="width:160px" /></el-form-item>
                  <el-form-item label="客户名称"><el-input v-model="propStack.custName" style="width:200px" /></el-form-item>
                  <el-form-item label="库存(吨)"><el-input-number v-model="propStack.stockAmount" :step="0.001" controls-position="right" /></el-form-item>
                  <el-form-item label="库存版本"><el-input :value="String(propStack.stockVersion)" disabled style="width:120px" /></el-form-item>
                </template>
                <el-form-item>
                  <el-button type="primary" size="small" @click="applyStackSave">保存</el-button>
                  <el-button v-if="stackPanelDisabled" type="success" size="small" plain @click="enableSelectedStack">启用</el-button>
                  <el-button v-else-if="stackPanelLinkedContract" type="warning" size="small" plain @click="disableSelectedStack">禁用</el-button>
                  <el-button v-else type="danger" size="small" plain @click="removeSelectedStack">删除</el-button>
                </el-form-item>
              </el-form>
            </template>
          </div>
        </div>
      </transition>
    </header>

    <div class="coal-body">
      <div class="coal-canvas nx-card">
        <div v-if="!canvasPanels.length" class="canvas-empty">
          <p>未获取到料场煤棚数据。</p>
          <p class="sub">请在 <strong>base_store</strong> 中维护仓库，<strong>仓库类型</strong> 为料场（store_type = 1）且有效。</p>
        </div>
        <div v-else class="dual-shed" :class="{ 'is-single': canvasPanels.length === 1 }">
          <div
            v-for="panel in canvasPanels"
            :key="panel.shed"
            class="shed-panel"
          >
            <h3 class="shed-title">
              {{ panel.store.storeName || "煤棚" }}
              <span class="dim">{{ panel.store.storeNo ? "编码 " + panel.store.storeNo + " · " : "" }}东西 {{ ew }}m × 南北 {{ ns }}m</span>
            </h3>
            <div class="svg-wrap">
              <div class="svg-zoom-layer" :style="zoomLayerStyle">
                <div class="svg-scale">
                  <svg
                    :class="['shed-svg', { 'mode-select': tool === 'select' }]"
                    :data-shed="panel.shed"
                    :viewBox="svgViewBox"
                    preserveAspectRatio="xMidYMid meet"
                    width="100%"
                    height="100%"
                    @mousedown="onPointerDown($event, panel.shed)"
                  >
                <defs>
                  <pattern :id="'grid' + panel.shed" width="10" height="10" patternUnits="userSpaceOnUse">
                    <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#E8EAED" stroke-width="0.06" />
                  </pattern>
                </defs>
                <!-- 坐标系：西北角为西东·北南起点，x 向东增大，y 向南增大 -->
                <g class="coord-axes" pointer-events="none">
                  <line :x1="0" :y1="0" :x2="ew" :y2="0" stroke="#94A3B8" stroke-width="0.28" stroke-dasharray="1.2 0.8" />
                  <line :x1="0" :y1="ns" :x2="ew" :y2="ns" stroke="#CBD5E1" stroke-width="0.2" />
                  <line :x1="0" :y1="0" :x2="0" :y2="ns" stroke="#94A3B8" stroke-width="0.28" stroke-dasharray="1.2 0.8" />
                  <line :x1="ew" :y1="0" :x2="ew" :y2="ns" stroke="#CBD5E1" stroke-width="0.2" />
                  <text :x="ew / 2" :y="-5" text-anchor="middle" fill="#475569" font-size="3.2" font-weight="600">北 (y = 0 起点)</text>
                  <text :x="ew / 2" :y="ns + 9" text-anchor="middle" fill="#475569" font-size="3.2" font-weight="600">南</text>
                  <text
                    :x="-11"
                    :y="ns / 2"
                    text-anchor="middle"
                    fill="#475569"
                    font-size="3.2"
                    font-weight="600"
                    :transform="'rotate(-90 -11 ' + ns / 2 + ')'"
                  >西 (x = 0 起点)</text>
                  <text
                    :x="ew + 11"
                    :y="ns / 2"
                    text-anchor="middle"
                    fill="#475569"
                    font-size="3.2"
                    font-weight="600"
                    :transform="'rotate(90 ' + (ew + 11) + ' ' + ns / 2 + ')'"
                  >东</text>
                  <text :x="1.2" :y="5.5" fill="#0F172A" font-size="2.6" font-weight="600">西北角 · 西东/北南坐标原点</text>
                  <text :x="ew - 0.5" :y="5" text-anchor="end" fill="#64748B" font-size="2.2">东向 +x →</text>
                  <text :x="1.2" :y="14" fill="#64748B" font-size="2.2">南向 +y ↓</text>
                </g>
                <rect
                  :x="0"
                  :y="0"
                  :width="ew"
                  :height="ns"
                  :fill="'url(#grid' + panel.shed + ')'"
                  stroke="#DCDFE6"
                  stroke-width="0.45"
                />
                <g>
                  <rect
                    v-for="z in zonesOfShed(panel.shed)"
                    :key="z.cid"
                    :x="z.x"
                    :y="z.y"
                    :width="z.w"
                    :height="z.h"
                    :fill="'rgba(37,99,235,0.14)'"
                    :stroke="isZoneSelected(panel.shed, z) ? '#2563EB' : '#3B82F6'"
                    :stroke-width="isZoneSelected(panel.shed, z) ? 0.55 : 0.32"
                    style="cursor:pointer"
                    @click.stop="clickZone(panel.shed, z)"
                  />
                  <text
                    v-for="z in zonesOfShed(panel.shed)"
                    :key="'t' + z.cid"
                    :x="z.x + z.w / 2"
                    :y="z.y + z.h / 2"
                    text-anchor="middle"
                    dominant-baseline="middle"
                    fill="#1F2937"
                    :font-size="z.w < 25 ? 2.5 : 3.2"
                  >{{ z.name }}</text>
                </g>
                <g>
                  <template v-for="z in zonesOfShed(panel.shed)">
                    <g :key="'sg' + z.cid">
                      <rect
                        v-for="s in z.stacks"
                        :key="s.cid"
                        :class="{ 'stack-rect-disabled': isStackDisabled(s) }"
                        :x="s.x"
                        :y="s.y"
                        :width="s.w"
                        :height="s.h"
                        fill="rgba(103,194,58,0.18)"
                        :stroke="isStackSelected(panel.shed, s) ? '#67C23A' : '#85CE61'"
                        :stroke-width="isStackSelected(panel.shed, s) ? 0.42 : 0.22"
                        style="cursor:pointer"
                        @click.stop="clickStack(panel.shed, z, s)"
                      />
                      <text
                        v-for="s in z.stacks"
                        :key="'st' + s.cid"
                        :x="s.x + s.w / 2"
                        :y="s.y + s.h / 2"
                        text-anchor="middle"
                        dominant-baseline="middle"
                        fill="#374151"
                        font-size="2"
                      >{{ s.name }}</text>
                    </g>
                  </template>
                </g>
                <rect
                  v-if="preview && preview.shed === panel.shed"
                  class="preview-rect"
                  :x="preview.x"
                  :y="preview.y"
                  :width="preview.w"
                  :height="preview.h"
                />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  storePage,
  storeAreaListByMainId,
  storeAreaInsert,
  storeAreaUpdate,
  storeAreaDelete,
  storeSubPage,
  storeSubInsert,
  storeSubUpdate,
  storeSubDelete,
  storeSubUpdateStock,
} from "./api";

const EW = 80;
const NS = 360;

export default {
  name: "CoalShedLayout",
  data() {
    return {
      ew: EW,
      ns: NS,
      svgViewBox: "-18 -12 108 388",
      westStore: null,
      eastStore: null,
      data: {
        west: { zones: [] },
        east: { zones: [] },
      },
      tool: "zone",
      selectedShed: null,
      selectedZoneCid: null,
      selectedStackCid: null,
      drawing: false,
      drawShed: null,
      drawStart: null,
      preview: null,
      mzShed: "west",
      mzName: "",
      mzX: 0,
      mzY: 0,
      mzW: 40,
      mzH: 120,
      msShed: "west",
      msZoneId: "",
      msName: "",
      msX: 0,
      msY: 0,
      msW: 10,
      msH: 15,
      gridRows: 2,
      gridCols: 4,
      propZone: null,
      propStack: null,
      propStackZone: null,
      dropOpen: false,
      dropKind: null,
      canvasZoom: 1,
    };
  },
  computed: {
    zoomLayerStyle() {
      return {
        transform: `scale(${this.canvasZoom})`,
        transformOrigin: "top center",
      };
    },
    dropPanelTitle() {
      const k = this.dropKind;
      if (k === "manualZone") return "手动添加库区";
      if (k === "manualStack") return "手动添加堆位";
      if (k === "editZone") return "库区属性" + (this.propZone && this.propZone.name ? " · " + this.propZone.name : "");
      if (k === "editStack") return "堆位属性" + (this.propStack && this.propStack.locationName ? " · " + this.propStack.locationName : "");
      return "";
    },
    canvasPanels() {
      const p = [];
      if (this.westStore) p.push({ shed: "west", store: this.westStore });
      if (this.eastStore) p.push({ shed: "east", store: this.eastStore });
      return p;
    },
    toolHint() {
      if (this.tool === "zone") return "在煤棚平面内拖拽绘制库区。";
      if (this.tool === "stack") return "请先在列表或画布选中库区，再在同一煤棚内拖拽堆位。";
      return "点击已创建的库区或堆位，顶部将展开属性面板。";
    },
    selectedZone() {
      if (!this.selectedShed || !this.selectedZoneCid || this.selectedStackCid) return null;
      const z = this.findZone(this.selectedShed, this.selectedZoneCid);
      return z || null;
    },
    selectedStack() {
      if (!this.selectedShed || !this.selectedStackCid || !this.selectedZoneCid) return null;
      const z = this.findZone(this.selectedShed, this.selectedZoneCid);
      if (!z) return null;
      return z.stacks.find((s) => s.cid === this.selectedStackCid) || null;
    },
    editStackShowBizFields() {
      const s = this.propStack && this.propStack._ref;
      return !!(s && this.stackHasContract(s));
    },
    stackPanelDisabled() {
      const s = this.selectedStack;
      return !!(s && (s.status === 0 || s.status === "0"));
    },
    stackPanelLinkedContract() {
      const s = this.selectedStack;
      return !!(s && this.stackHasContract(s));
    },
    stackTheoryCap() {
      const p = this.propStack;
      if (!p || this.dropKind !== "editStack") return "—";
      const t = this.calcStackCapTons(p.layoutW, p.layoutH);
      return (Math.round(t * 1000) / 1000).toString();
    },
  },
  watch: {
    mzShed() {
      this.resetManualZoneFields();
    },
    msShed() {
      this.resetManualStackFields();
    },
  },
  mounted() {
    this.bootstrapFromBaseStore();
    window.addEventListener("mousemove", this.onPointerMove);
    window.addEventListener("mouseup", this.onPointerUp);
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onPointerMove);
    window.removeEventListener("mouseup", this.onPointerUp);
  },
  methods: {
    /** 与 3D 场景一致：占地(m²)×净空30m×1t/m³ */
    calcStackCapTons(w, h) {
      const a = Math.max(Number(w) || 0, 0);
      const b = Math.max(Number(h) || 0, 0);
      return a * b * 30;
    },
    onCapOverrideSwitch(enabled) {
      if (!this.propStack) return;
      if (enabled) {
        const t = this.calcStackCapTons(this.propStack.layoutW, this.propStack.layoutH);
        this.$set(this.propStack, "stockCapTons", Math.max(t, 0.001));
      }
    },
    shedOptionLabel(shed) {
      const s = shed === "west" ? this.westStore : this.eastStore;
      if (!s) return shed === "west" ? "左侧煤棚(无数据)" : "右侧煤棚(无数据)";
      const leftRight = shed === "west" ? "左" : "右";
      return `${leftRight} · ${s.storeName || s.storeNo || "煤棚"}`;
    },
    shedTitle(shed) {
      const s = shed === "west" ? this.westStore : this.eastStore;
      return s && (s.storeName || s.storeNo) ? s.storeName || s.storeNo : shed === "west" ? "左煤棚" : "右煤棚";
    },
    zonesOfShed(shed) {
      return this.data[shed] && this.data[shed].zones ? this.data[shed].zones : [];
    },
    findZone(shed, zoneCid) {
      const bucket = this.data[shed];
      if (!bucket || !bucket.zones) return undefined;
      return bucket.zones.find((z) => z.cid === zoneCid);
    },
    isZoneSelected(shed, z) {
      return this.tool !== "zone" && this.selectedShed === shed && this.selectedZoneCid === z.cid && !this.selectedStackCid;
    },
    isStackSelected(shed, s) {
      return this.selectedShed === shed && this.selectedStackCid === s.cid;
    },
    stackHasContract(s) {
      if (!s) return false;
      const v = s.contractExtId;
      if (v == null || v === "") return false;
      const n = Number(v);
      return Number.isFinite(n) && n > 0;
    },
    isStackDisabled(s) {
      return !!(s && (s.status === 0 || s.status === "0"));
    },
    zoneHasContractStack(z) {
      return !!(z && z.stacks && z.stacks.some((s) => this.stackHasContract(s)));
    },
    resetManualZoneFields() {
      this.mzName = "";
      this.mzX = 0;
      this.mzY = 0;
      this.mzW = 40;
      this.mzH = 120;
    },
    resetManualStackFields() {
      this.msName = "";
      this.msX = 0;
      this.msY = 0;
      this.msW = 10;
      this.msH = 15;
      this.syncMsZone();
    },
    zoomIn() {
      this.canvasZoom = Math.min(2.5, Math.round((this.canvasZoom + 0.15) * 100) / 100);
    },
    zoomOut() {
      this.canvasZoom = Math.max(0.35, Math.round((this.canvasZoom - 0.15) * 100) / 100);
    },
    zoomReset() {
      this.canvasZoom = 1;
    },
    setTool(t) {
      this.tool = t;
      if (this.dropOpen && (this.dropKind === "manualZone" || this.dropKind === "manualStack")) {
        this.closeDropPanel();
      }
    },
    toggleDrop(kind) {
      if (this.dropOpen && this.dropKind === kind) {
        this.closeDropPanel();
      } else {
        this.dropKind = kind;
        this.dropOpen = true;
        if (kind === "manualStack") this.syncMsZone();
      }
    },
    closeDropPanel() {
      this.dropOpen = false;
      this.dropKind = null;
    },
    /** 从 base_store 加载料场（store_type=1），按编码排序；第一条为左、第二条为右 */
    async bootstrapFromBaseStore() {
      try {
        const res = await storePage({ page: 1, size: 500, storeType: 1, status: 1 });
        const page = res.data.data || {};
        let list = page.records || [];
        list = list.filter((s) => s == null ? false : (s.status === 1 || s.status === "1"));
        list.sort((a, b) => String(a.storeNo || "").localeCompare(String(b.storeNo || ""), "zh-Hans-CN"));
        this.westStore = list[0] || null;
        this.eastStore = list.length >= 2 ? list[1] : null;
        if (!this.eastStore && this.mzShed === "east") this.mzShed = "west";
        if (!this.eastStore && this.msShed === "east") this.msShed = "west";
        await this.reloadAll();
      } catch (e) {
        this.westStore = null;
        this.eastStore = null;
      }
    },
    storeByShed(shed) {
      return shed === "west" ? this.westStore : this.eastStore;
    },
    mainIdOfShed(shed) {
      const s = this.storeByShed(shed);
      return s && s.id != null ? String(s.id) : "";
    },
    async reloadAll() {
      try {
        if (this.westStore) await this.loadShed("west", String(this.westStore.id));
        else this.$set(this.data, "west", { zones: [] });
        if (this.eastStore) await this.loadShed("east", String(this.eastStore.id));
        else this.$set(this.data, "east", { zones: [] });
        this.selectedShed = null;
        this.selectedZoneCid = null;
        this.selectedStackCid = null;
        this.propZone = null;
        this.propStack = null;
        this.closeDropPanel();
        this.syncMsZone();
      } catch (e) {
        /* axios 已提示 */
      }
    },
    async loadShed(shed, mainId) {
      const areasRes = await storeAreaListByMainId(mainId);
      const areas = areasRes.data.data || [];
      const zones = [];
      for (const a of areas) {
        const z = {
          cid: "z_" + a.id,
          dbId: a.id,
          name: a.areaName || "库区",
          x: Number(a.posX) || 0,
          y: Number(a.posY) || 0,
          w: Number(a.posW) || 0,
          h: Number(a.posH) || 0,
          stacks: [],
        };
        const subRes = await storeSubPage({
          page: 1,
          size: 2000,
          mainId,
          areaId: a.id,
        });
        const subPage = subRes.data.data || {};
        const subs = subPage.records || [];
        z.stacks = subs.map((row) => ({
          cid: "s_" + row.id,
          dbId: row.id,
          name: row.locationName || row.locationCode || "堆位",
          x: Number(row.layoutX) || 0,
          y: Number(row.layoutY) || 0,
          w: Number(row.layoutW) || 1,
          h: Number(row.layoutH) || 1,
          locationCode: row.locationCode,
          contractExtId: row.contractExtId != null ? String(row.contractExtId) : "",
          custNo: row.custNo || "",
          custName: row.custName || "",
          stockAmount: row.stockAmount != null ? Number(row.stockAmount) : null,
          stockCapTons:
            row.stockCapTons != null && row.stockCapTons !== ""
              ? Number(row.stockCapTons)
              : null,
          stockVersion: row.stockVersion != null ? Number(row.stockVersion) : 0,
          status: row.status != null ? Number(row.status) : 1,
        }));
        zones.push(z);
      }
      this.$set(this.data, shed, { zones });
    },
    clampRect(x, y, w, h) {
      let nx = Math.max(0, Math.min(x, EW));
      let ny = Math.max(0, Math.min(y, NS));
      let nw = Math.max(0, w);
      let nh = Math.max(0, h);
      if (nx + nw > EW) nw = EW - nx;
      if (ny + nh > NS) nh = NS - ny;
      return { x: nx, y: ny, w: nw, h: nh };
    },
    normalizeRect(x0, y0, x1, y1) {
      const x = Math.min(x0, x1);
      const y = Math.min(y0, y1);
      return this.clampRect(x, y, Math.abs(x1 - x0), Math.abs(y1 - y0));
    },
    insideZone(z, r) {
      return (
        r.x >= z.x - 0.01 &&
        r.y >= z.y - 0.01 &&
        r.x + r.w <= z.x + z.w + 0.01 &&
        r.y + r.h <= z.y + z.h + 0.01
      );
    },
    ptFromEvent(ev, svg) {
      const rect = svg.getBoundingClientRect();
      const vb = svg.viewBox && svg.viewBox.baseVal;
      if (!vb || !vb.width || !rect.width) return { x: 0, y: 0 };
      const x = ((ev.clientX - rect.left) / rect.width) * vb.width + vb.x;
      const y = ((ev.clientY - rect.top) / rect.height) * vb.height + vb.y;
      return { x, y };
    },
    onPointerDown(ev, shed) {
      const svg = ev.currentTarget;
      if (this.tool === "select") return;
      const p = this.ptFromEvent(ev, svg);
      if (p.x < 0 || p.x > EW || p.y < 0 || p.y > NS) return;
      if (this.tool === "stack") {
        if (!this.selectedZoneCid || this.selectedShed !== shed) {
          this.$message.warning("请先在同一煤棚内选中库区。");
          return;
        }
      }
      this.drawing = true;
      this.drawShed = shed;
      this.drawStart = { x: p.x, y: p.y };
      this.preview = { shed, x: p.x, y: p.y, w: 0, h: 0 };
      ev.preventDefault();
    },
    onPointerMove(ev) {
      if (!this.drawing || !this.drawStart || !this.drawShed) return;
      const el = document.querySelector(`svg.shed-svg[data-shed="${this.drawShed}"]`);
      if (!el) return;
      const p = this.ptFromEvent(ev, el);
      const r = this.normalizeRect(this.drawStart.x, this.drawStart.y, p.x, p.y);
      this.preview = { shed: this.drawShed, ...r };
    },
    async onPointerUp(ev) {
      if (!this.drawing || !this.drawStart || !this.drawShed) return;
      const shed = this.drawShed;
      const el = document.querySelector(`svg.shed-svg[data-shed="${shed}"]`);
      const p = el ? this.ptFromEvent(ev, el) : { x: this.drawStart.x, y: this.drawStart.y };
      const r = this.normalizeRect(this.drawStart.x, this.drawStart.y, p.x, p.y);
      this.preview = null;
      this.drawing = false;
      this.drawStart = null;
      this.drawShed = null;
      const minSize = 1.5;
      if (r.w < minSize || r.h < minSize) return;

      const store = this.storeByShed(shed);
      const mainId = this.mainIdOfShed(shed);
      if (!store || !mainId) {
        this.$message.warning("未获取到煤棚主数据，请检查 base_store 料场配置。");
        return;
      }

      try {
        if (this.tool === "zone") {
          const name = "库区" + (this.data[shed].zones.length + 1);
          const payload = {
            mainId,
            tenantId: store.tenantId,
            deptId: store.deptId,
            areaName: name,
            posX: r.x,
            posY: r.y,
            posW: r.w,
            posH: r.h,
            remark: "",
          };
          const res = await storeAreaInsert(payload);
          const id = res.data.data;
          const z = {
            cid: "z_" + id,
            dbId: id,
            name,
            x: r.x,
            y: r.y,
            w: r.w,
            h: r.h,
            stacks: [],
          };
          this.data[shed].zones.push(z);
          this.selectedShed = shed;
          this.selectedZoneCid = z.cid;
          this.selectedStackCid = null;
          this.openPropZone(z);
          this.syncMsZone();
        } else if (this.tool === "stack") {
          const z = this.findZone(shed, this.selectedZoneCid);
          if (!z || !this.insideZone(z, r)) {
            this.$message.warning("堆位须完全落在所选库区内。");
            return;
          }
          const code = `SUB-${z.dbId}-${Date.now()}`;
          const sname = "堆位" + (z.stacks.length + 1);
          const row = this.buildSubPayload(store, z, {
            locationCode: code,
            locationName: sname,
            layoutX: r.x,
            layoutY: r.y,
            layoutW: r.w,
            layoutH: r.h,
            stockAmount: 0,
          });
          const res = await storeSubInsert(row);
          const nid = res.data.data;
          const s = {
            cid: "s_" + nid,
            dbId: nid,
            name: sname,
            x: r.x,
            y: r.y,
            w: r.w,
            h: r.h,
            locationCode: code,
            contractExtId: "",
            custNo: "",
            custName: "",
            stockAmount: 0,
            stockVersion: 0,
            status: 1,
          };
          z.stacks.push(s);
          this.selectedStackCid = s.cid;
          this.openPropStack(z, s);
        }
      } catch (e) {
        /* message */
      }
      ev.preventDefault();
    },
    buildSubPayload(store, zone, extra) {
      return {
        mainId: store.id,
        tenantId: store.tenantId,
        deptId: store.deptId,
        storeType: store.storeType != null ? store.storeType : 1,
        storeNo: store.storeNo,
        storeName: store.storeName,
        areaId: zone.dbId,
        ...extra,
      };
    },
    clickZone(shed, z) {
      this.selectedShed = shed;
      this.selectedZoneCid = z.cid;
      this.selectedStackCid = null;
      this.openPropZone(z);
      this.syncMsZone();
    },
    clickStack(shed, z, s) {
      this.selectedShed = shed;
      this.selectedZoneCid = z.cid;
      this.selectedStackCid = s.cid;
      this.openPropStack(z, s);
      this.syncMsZone();
    },
    openPropZone(z) {
      this.propZone = { name: z.name, x: z.x, y: z.y, w: z.w, h: z.h, _ref: z };
      this.propStack = null;
      this.dropKind = "editZone";
      this.dropOpen = true;
    },
    openPropStack(z, s) {
      const theory = this.calcStackCapTons(s.w, s.h);
      const capOverride =
        s.stockCapTons != null && s.stockCapTons !== "" && Number.isFinite(Number(s.stockCapTons));
      this.propStack = {
        locationName: s.name,
        layoutX: s.x,
        layoutY: s.y,
        layoutW: s.w,
        layoutH: s.h,
        contractExtId: s.contractExtId || "",
        custNo: s.custNo || "",
        custName: s.custName || "",
        stockAmount: s.stockAmount,
        stockVersion: s.stockVersion,
        capOverrideEnabled: capOverride,
        stockCapTons: capOverride ? Number(s.stockCapTons) : theory,
        _ref: s,
      };
      this.propStackZone = z;
      this.propZone = null;
      this.dropKind = "editStack";
      this.dropOpen = true;
    },
    syncMsZone() {
      const zones = this.zonesOfShed(this.msShed);
      if (!zones.length) {
        this.msZoneId = "";
        return;
      }
      if (this.selectedShed === this.msShed && this.selectedZoneCid) {
        this.msZoneId = this.selectedZoneCid;
      } else if (!zones.find((z) => z.cid === this.msZoneId)) {
        this.msZoneId = zones[0].cid;
      }
    },
    async addZoneManual() {
      const shed = this.mzShed;
      const store = this.storeByShed(shed);
      const mainId = this.mainIdOfShed(shed);
      if (!store || !mainId) {
        this.$message.warning("该侧煤棚无数据。");
        return;
      }
      const r = this.clampRect(this.mzX, this.mzY, this.mzW, this.mzH);
      if (r.w < 0.5 || r.h < 0.5) {
        this.$message.warning("请填写有效宽高。");
        return;
      }
      const name = (this.mzName || "").trim() || "库区" + (this.data[shed].zones.length + 1);
      const payload = {
        mainId,
        tenantId: store.tenantId,
        deptId: store.deptId,
        areaName: name,
        posX: r.x,
        posY: r.y,
        posW: r.w,
        posH: r.h,
      };
      const res = await storeAreaInsert(payload);
      const id = res.data.data;
      const z = {
        cid: "z_" + id,
        dbId: id,
        name,
        x: r.x,
        y: r.y,
        w: r.w,
        h: r.h,
        stacks: [],
      };
      this.data[shed].zones.push(z);
      this.selectedShed = shed;
      this.selectedZoneCid = z.cid;
      this.openPropZone(z);
      this.syncMsZone();
    },
    async addStackManual() {
      const shed = this.msShed;
      const z = this.findZone(shed, this.msZoneId);
      if (!z) {
        this.$message.warning("请选择有效库区。");
        return;
      }
      const store = this.storeByShed(shed);
      const r = this.clampRect(this.msX, this.msY, this.msW, this.msH);
      if (!this.insideZone(z, r)) {
        this.$message.warning("堆位须完全落在库区内。");
        return;
      }
      const name = (this.msName || "").trim() || "堆位" + (z.stacks.length + 1);
      const code = `SUB-${z.dbId}-${Date.now()}`;
      const row = this.buildSubPayload(store, z, {
        locationCode: code,
        locationName: name,
        layoutX: r.x,
        layoutY: r.y,
        layoutW: r.w,
        layoutH: r.h,
        stockAmount: 0,
      });
      const res = await storeSubInsert(row);
      const nid = res.data.data;
      const s = {
        cid: "s_" + nid,
        dbId: nid,
        name,
        x: r.x,
        y: r.y,
        w: r.w,
        h: r.h,
        locationCode: code,
        contractExtId: "",
        custNo: "",
        custName: "",
        stockAmount: 0,
        stockVersion: 0,
        status: 1,
      };
      z.stacks.push(s);
      this.selectedShed = shed;
      this.selectedZoneCid = z.cid;
      this.selectedStackCid = s.cid;
      this.openPropStack(z, s);
    },
    async applyZone() {
      const z = this.propZone && this.propZone._ref;
      if (!z || !z.dbId) return;
      const r = this.clampRect(this.propZone.x, this.propZone.y, this.propZone.w, this.propZone.h);
      await storeAreaUpdate({
        id: z.dbId,
        areaName: this.propZone.name,
        posX: r.x,
        posY: r.y,
        posW: r.w,
        posH: r.h,
      });
      z.name = this.propZone.name;
      z.x = r.x;
      z.y = r.y;
      z.w = r.w;
      z.h = r.h;
      this.$message.success("库区已保存");
    },
    async removeSelectedZone() {
      const z = this.selectedZone;
      if (!z || !z.dbId) return;
      if (this.zoneHasContractStack(z)) {
        this.$message.warning("库区内存在已关联合同的堆位，无法删除库区。请先禁用或解除关联后再试。");
        return;
      }
      try {
        await this.$confirm("删除该库区及其全部堆位？", "提示", { type: "warning" });
      } catch {
        return;
      }
      for (const s of z.stacks) {
        if (s.dbId) await storeSubDelete(s.dbId);
      }
      await storeAreaDelete(z.dbId);
      const shed = this.selectedShed;
      this.data[shed].zones = this.data[shed].zones.filter((x) => x.cid !== z.cid);
      this.selectedZoneCid = null;
      this.propZone = null;
      this.closeDropPanel();
      this.syncMsZone();
      this.$message.success("已删除");
    },
    async applyStackSave() {
      const s = this.propStack && this.propStack._ref;
      const z = this.propStackZone;
      if (!s || !z || !s.dbId) return;
      const r = this.clampRect(
        this.propStack.layoutX,
        this.propStack.layoutY,
        this.propStack.layoutW,
        this.propStack.layoutH
      );
      if (!this.insideZone(z, r)) {
        this.$message.warning("堆位须落在库区内。");
        return;
      }
      const showBiz = this.stackHasContract(s);
      const payload = {
        id: s.dbId,
        locationName: this.propStack.locationName,
        layoutX: r.x,
        layoutY: r.y,
        layoutW: r.w,
        layoutH: r.h,
      };
      if (this.propStack.capOverrideEnabled) {
        const cap = Number(this.propStack.stockCapTons);
        if (!Number.isFinite(cap) || cap <= 0) {
          this.$message.warning("自定义库存容量上限须为大于 0 的数值。");
          return;
        }
        payload.stockCapTons = cap;
      } else {
        payload.stockCapTonsUseFormula = true;
      }
      let extStr = "";
      if (showBiz) {
        extStr = (this.propStack.contractExtId || "").trim();
        let contractExtId = null;
        if (extStr) {
          const n = Number(extStr);
          contractExtId = Number.isFinite(n) ? n : null;
        }
        payload.contractExtId = contractExtId;
        payload.custNo = this.propStack.custNo || null;
        payload.custName = this.propStack.custName || null;
      }
      await storeSubUpdate(payload);
      s.name = this.propStack.locationName;
      s.x = r.x;
      s.y = r.y;
      s.w = r.w;
      s.h = r.h;
      s.stockCapTons = this.propStack.capOverrideEnabled ? Number(this.propStack.stockCapTons) : null;
      if (showBiz) {
        s.contractExtId = extStr;
        s.custNo = this.propStack.custNo;
        s.custName = this.propStack.custName;
        const ver = Number(this.propStack.stockVersion);
        await storeSubUpdateStock({
          id: s.dbId,
          stockAmount: this.propStack.stockAmount != null ? this.propStack.stockAmount : 0,
          stockVersion: ver,
        });
        s.stockAmount = this.propStack.stockAmount;
        s.stockVersion = ver + 1;
        this.propStack.stockVersion = s.stockVersion;
      }
      this.$message.success("已保存");
    },
    async disableSelectedStack() {
      const s = this.selectedStack;
      if (!s || !s.dbId) return;
      try {
        await this.$confirm("禁用后堆位仍保留且合同关联不变，业务侧将按停用处理。是否继续？", "提示", { type: "warning" });
      } catch {
        return;
      }
      await storeSubUpdate({ id: s.dbId, status: 0 });
      s.status = 0;
      this.$message.success("已禁用");
    },
    async enableSelectedStack() {
      const s = this.selectedStack;
      if (!s || !s.dbId) return;
      await storeSubUpdate({ id: s.dbId, status: 1 });
      s.status = 1;
      this.$message.success("已启用");
    },
    async removeSelectedStack() {
      const s = this.selectedStack;
      if (!s || !s.dbId) return;
      if (this.stackHasContract(s)) {
        this.$message.warning("已关联合同的堆位不可删除，请使用禁用。");
        return;
      }
      await storeSubDelete(s.dbId);
      const z = this.findZone(this.selectedShed, this.selectedZoneCid);
      if (z) z.stacks = z.stacks.filter((x) => x.cid !== s.cid);
      this.selectedStackCid = null;
      this.propStack = null;
      this.closeDropPanel();
      this.$message.success("已删除堆位");
    },
    async gridStacks() {
      const z = this.selectedZone;
      if (!z || !z.dbId) {
        this.$message.warning("请先选中库区。");
        return;
      }
      if (this.zoneHasContractStack(z)) {
        this.$message.warning("库区内存在已关联合同的堆位，无法按网格重新生成。请先禁用或删除无合同堆位后再试。");
        return;
      }
      const shed = this.selectedShed;
      const store = this.storeByShed(shed);
      const rows = Math.max(1, parseInt(this.gridRows, 10) || 1);
      const cols = Math.max(1, parseInt(this.gridCols, 10) || 1);
      for (const s of z.stacks) {
        if (s.dbId) await storeSubDelete(s.dbId);
      }
      z.stacks = [];
      const cw = z.w / cols;
      const ch = z.h / rows;
      let n = 0;
      for (let ri = 0; ri < rows; ri++) {
        for (let ci = 0; ci < cols; ci++) {
          n++;
          const r = { x: z.x + ci * cw, y: z.y + ri * ch, w: cw, h: ch };
          const code = `SUB-${z.dbId}-G${n}-${Date.now()}`;
          const name = `${z.name}-${n}`;
          const row = this.buildSubPayload(store, z, {
            locationCode: code,
            locationName: name,
            layoutX: r.x,
            layoutY: r.y,
            layoutW: r.w,
            layoutH: r.h,
            stockAmount: 0,
          });
          const res = await storeSubInsert(row);
          const nid = res.data.data;
          z.stacks.push({
            cid: "s_" + nid,
            dbId: nid,
            name,
            x: r.x,
            y: r.y,
            w: r.w,
            h: r.h,
            locationCode: code,
            contractExtId: "",
            custNo: "",
            custName: "",
            stockAmount: 0,
            stockVersion: 0,
            status: 1,
          });
        }
      }
      this.$message.success("网格堆位已生成");
    },
    async clearStacksInZone() {
      const z = this.selectedZone;
      if (!z) {
        this.$message.warning("请先选中库区。");
        return;
      }
      if (this.zoneHasContractStack(z)) {
        this.$message.warning("库区内存在已关联合同的堆位，无法清空。请先禁用或单独处理后再试。");
        return;
      }
      try {
        await this.$confirm("清空该库区下全部堆位？", "提示", { type: "warning" });
      } catch {
        return;
      }
      for (const s of z.stacks) {
        if (s.dbId) await storeSubDelete(s.dbId);
      }
      z.stacks = [];
      this.selectedStackCid = null;
      this.propStack = null;
    },
  },
};
</script>

<style scoped lang="scss">
.coal-shed-layout {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 140px);
  box-sizing: border-box;
  background: #f0f2f5;
  overflow: hidden;
}

.coal-ribbon {
  flex-shrink: 0;
  position: relative;
  z-index: 2;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.12);
}

.ribbon-inner {
  display: flex;
  align-items: stretch;
  gap: 0;
  padding: 8px 16px 10px;
  flex-wrap: wrap;
}

.ribbon-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding-right: 16px;
}

.ribbon-group-title {
  font-size: 11px;
  color: #909399;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  user-select: none;
}

.ribbon-btns {
  display: flex;
  gap: 4px;
}

.ribbon-btns--row {
  flex-direction: row;
  align-items: center;
}

.ribbon-btn--compact {
  min-width: 56px;
  padding: 6px 8px 4px;
}

.zoom-readout {
  min-width: 44px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  padding: 0 4px;
  user-select: none;
}

.ribbon-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 8px 10px 6px;
  border: 1px solid transparent;
  border-radius: 4px;
  background: #f5f7fa;
  cursor: pointer;
  color: #303133;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.ribbon-btn:hover {
  background: #ebeef5;
  border-color: #dcdfe6;
}

.ribbon-btn.is-active {
  background: #ecf5ff;
  border-color: #b3d8ff;
  color: #409eff;
}

.ribbon-btn-ico {
  font-size: 18px;
  line-height: 1;
  margin-bottom: 4px;
  opacity: 0.85;
}

.ribbon-btn-text {
  font-size: 12px;
  line-height: 1.2;
  text-align: center;
}

.ribbon-sep {
  width: 1px;
  align-self: stretch;
  min-height: 48px;
  background: #e4e7ed;
  margin: 0 8px;
}

.ribbon-hint {
  flex: 1;
  min-width: 200px;
  margin: 0;
  padding: 8px 0 0;
  font-size: 12px;
  color: #606266;
  line-height: 1.5;
  align-self: center;
}

.coal-drop-panel {
  border-top: 1px solid #ebeef5;
  background: #fafbfc;
  max-height: 50vh;
  overflow-x: auto;
  overflow-y: auto;
}

.drop-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
}

.drop-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.drop-close {
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: #909399;
  cursor: pointer;
  padding: 0 4px;
}

.drop-close:hover {
  color: #409eff;
}

.drop-panel-body {
  padding: 12px 16px 14px;
}

.drop-form.inline-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
}

.drop-form.inline-form ::v-deep .el-form-item {
  margin-bottom: 10px;
  margin-right: 8px;
}

.drop-readonly {
  font-size: 13px;
  color: #606266;
}

.drop-readonly.warn {
  color: #e6a23c;
  font-weight: 600;
}

.cap-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.cap-input {
  width: 180px;
}

.drop-subblock {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e4e7ed;
}

.drop-subtitle {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
}

.coal-drop-enter-active,
.coal-drop-leave-active {
  transition: opacity 0.2s ease, transform 0.22s ease;
}

.coal-drop-enter,
.coal-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.coal-body {
  flex: 1;
  min-height: 0;
  display: flex;
  padding: 12px;
  padding-top: 10px;
  overflow-x: auto;
  overflow-y: auto;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
}

.nx-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.coal-canvas {
  width: 100%;
  flex: 1 0 auto;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}

.canvas-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 14px;
  text-align: center;
  padding: 24px;
}

.canvas-empty .sub {
  margin-top: 8px;
  font-size: 13px;
  max-width: 420px;
}

.dual-shed {
  flex: 1 1 auto;
  display: flex;
  gap: 12px;
  min-height: 0;
  align-items: stretch;
}

.dual-shed.is-single .shed-panel {
  max-width: none;
}

.shed-panel {
  flex: 1;
  min-width: 280px;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fafbfc;
  overflow: hidden;
}

.shed-title {
  font-size: 14px;
  margin: 0 0 8px;
  font-weight: 600;
  flex-shrink: 0;
}

.shed-title .dim {
  font-size: 12px;
  font-weight: 400;
  color: #909399;
}

.svg-wrap {
  flex: 1 1 auto;
  width: 100%;
  min-height: 200px;
  position: relative;
  background: #fafbfc;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: auto;
}

.svg-zoom-layer {
  width: 100%;
  transition: transform 0.12s ease-out;
}

.svg-scale {
  width: 100%;
  aspect-ratio: 108 / 388;
  display: block;
}

.shed-svg {
  display: block;
  width: 100%;
  height: 100%;
  cursor: crosshair;
}

.shed-svg.mode-select {
  cursor: default;
}

.preview-rect {
  fill: rgba(245, 158, 11, 0.2);
  stroke: #f59e0b;
  stroke-width: 0.35;
  stroke-dasharray: 1.2 1.2;
  pointer-events: none;
}

.shed-svg rect.stack-rect-disabled {
  fill: rgba(144, 147, 153, 0.4) !important;
  stroke: #909399 !important;
}

</style>
